import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  consolidateBrandStyleguide,
  createBrandProfile,
  deleteManagedBrandAsset,
  deleteBrandProfile,
  crawlBrandIdentityWebsite,
  listBrandAssets,
  listBrandProfiles,
  listBrandIdentitySources,
  updateManagedBrandAsset,
  updateBrandProfile,
  updateBrandStyleguide,
  uploadManagedBrandAsset,
  uploadBrandAsset,
  uploadBrandIdentitySource,
  uploadBrandSwatch,
  type BrandAsset,
  type BrandAssetType,
  type BrandCreateInput,
  type BrandIdentitySource,
  type BrandIdentitySourceType,
  type BrandProfile,
  type BrandStyleguide,
  type BrandUpdateInput,
} from './brand-api'

export const brandKeys = {
  all: ['brand'] as const,
  profiles: () => [...brandKeys.all, 'profiles'] as const,
  assets: (brandId: string) => [...brandKeys.all, 'assets', brandId] as const,
  identitySources: (brandId: string) => [...brandKeys.all, 'identity-sources', brandId] as const,
}

export function useBrandProfiles() {
  return useQuery({
    queryKey: brandKeys.profiles(),
    queryFn: () => listBrandProfiles(),
    staleTime: 30_000,
  })
}

export function useCreateBrandProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BrandCreateInput) => createBrandProfile(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useUpdateBrandProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: BrandUpdateInput }) =>
      updateBrandProfile(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useDeleteBrandProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteBrandProfile(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useUploadBrandAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, kind, file }: { id: string; kind: 'logo' | 'gradient'; file: File }) =>
      uploadBrandAsset(id, kind, file),
    onSuccess: (profile: BrandProfile) => {
      qc.setQueryData<BrandProfile[]>(brandKeys.profiles(), (prev) =>
        prev ? prev.map((p) => (p.id === profile.id ? profile : p)) : prev,
      )
    },
  })
}

export function useUploadBrandSwatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      name,
      color,
      file,
    }: {
      id: string
      name: string
      color: string
      file: File
    }) => uploadBrandSwatch(id, name, color, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useBrandAssets(brandId: string | null | undefined) {
  return useQuery({
    queryKey: brandKeys.assets(brandId ?? ''),
    queryFn: () => listBrandAssets(brandId!),
    enabled: !!brandId,
    staleTime: 15_000,
  })
}

export function useUploadManagedBrandAsset(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      file: File
      assetType: BrandAssetType
      slotRole?: string
      displayOrder?: number
    }) => uploadManagedBrandAsset(brandId, input),
    onSuccess: (asset: BrandAsset) => {
      qc.setQueryData<BrandAsset[]>(brandKeys.assets(brandId), (prev) =>
        prev ? [...prev.filter((item) => item.id !== asset.id), asset] : [asset],
      )
      void qc.invalidateQueries({ queryKey: brandKeys.assets(brandId) })
    },
  })
}

export function useUpdateManagedBrandAsset(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      assetId,
      input,
    }: {
      assetId: string
      input: { slotRole?: string | null; displayOrder?: number }
    }) => updateManagedBrandAsset(brandId, assetId, input),
    onSuccess: (asset: BrandAsset) => {
      qc.setQueryData<BrandAsset[]>(brandKeys.assets(brandId), (prev) =>
        prev ? prev.map((item) => (item.id === asset.id ? asset : item)) : prev,
      )
    },
  })
}

export function useDeleteManagedBrandAsset(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assetId: string) => deleteManagedBrandAsset(brandId, assetId),
    onSuccess: (_result, assetId) => {
      qc.setQueryData<BrandAsset[]>(brandKeys.assets(brandId), (prev) =>
        prev ? prev.filter((item) => item.id !== assetId) : prev,
      )
      void qc.invalidateQueries({ queryKey: brandKeys.assets(brandId) })
    },
  })
}

export function useBrandIdentitySources(brandId: string | null | undefined) {
  return useQuery({
    queryKey: brandKeys.identitySources(brandId ?? ''),
    queryFn: () => listBrandIdentitySources(brandId!),
    enabled: !!brandId,
    staleTime: 15_000,
  })
}

export function useUploadBrandIdentitySource(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      file: File
      sourceType: Exclude<BrandIdentitySourceType, 'website'>
      sourceName?: string
      skipAnalyze?: boolean
    }) => uploadBrandIdentitySource(brandId, input),
    onSuccess: (source: BrandIdentitySource) => {
      qc.setQueryData<BrandIdentitySource[]>(brandKeys.identitySources(brandId), (prev) =>
        prev ? [source, ...prev.filter((item) => item.id !== source.id)] : [source],
      )
      void qc.invalidateQueries({ queryKey: brandKeys.identitySources(brandId) })
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useCrawlBrandIdentityWebsite(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { url: string; depth?: number; skipAnalyze?: boolean }) =>
      crawlBrandIdentityWebsite(brandId, input),
    onSuccess: (source: BrandIdentitySource) => {
      qc.setQueryData<BrandIdentitySource[]>(brandKeys.identitySources(brandId), (prev) =>
        prev ? [source, ...prev.filter((item) => item.id !== source.id)] : [source],
      )
      void qc.invalidateQueries({ queryKey: brandKeys.identitySources(brandId) })
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useConsolidateBrandStyleguide(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => consolidateBrandStyleguide(brandId),
    onSuccess: (styleguide) => {
      qc.setQueryData<BrandProfile[]>(brandKeys.profiles(), (prev) =>
        prev
          ? prev.map((profile) =>
              profile.id === brandId
                ? { ...profile, brand_styleguide: styleguide }
                : profile,
            )
          : prev,
      )
      void qc.invalidateQueries({ queryKey: brandKeys.identitySources(brandId) })
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useUpdateBrandStyleguide(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BrandStyleguide) => updateBrandStyleguide(brandId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}
